import { useEffect, useState, useRef, useMemo } from "react";
import exceljs from "exceljs";
import {saveAs} from "file-saver";
//-------------------------------------------
import Resize from "src/components/common/resize";
import { useMyStore } from "src/components/main/mainStore";
import {
    myAxios, MyDate, mimeTypeMap, 
    MyFormData, onlyUnique, weeknum, getDatefromWeekNum, numberReducer,
    appTitle
} from "src/components/common/functions";
import Chart from "./chart";
//-------------------------------------------
export default function LaborUsage({tabTitle}){
    const setShowLoader = useMyStore(state => state.setShowLoader);
    const user = useMyStore(state => state.user);
    const departments = [
        "Potholing", 
        "Locating", 
        "Scanning&Coring", 
        "CCTV", 
        "Mapping&Surveying", 
        "QA/QC", 
        "Traffic Control", 
        "Hydro Vac Excavation", 
        "Straight Vacuum Services", 
        "LA", 
        "Arizona"
    ];
    const myChart = useRef(null);
    const onComplete = () => {
        var chartInstance = myChart.current.chart,
        ctx = chartInstance.ctx;
        ctx.font = Chart.helpers?.fontString(Chart.defaults.global.defaultFontSize, Chart.defaults.global.defaultFontStyle, Chart.defaults.global.defaultFontFamily);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        myChart.current.data.datasets.forEach((dataset, i) => {
            var meta = chartInstance.controller.getDatasetMeta(i);
            meta.data.forEach(function(bar, index){
            var data = dataset.data[index];
            if(data > 0){
                ctx.fillText(data, bar._model.x, bar._model.y - 5);
            }
            });
        });
    }
    //-------------------------------------------------------------------------
    const weeks = useMemo(() => {
        const dateArray = [];
        const min = new Date("2021/03/08");
        const max = new Date();
        while(min < max){
            dateArray.push(weeknum(max));
            max.setDate(max.getDate() - 7);
        }
        return dateArray;
    });
    const [showLoading, setShowLoading] = useState(false);
    const [curData, setData] = useState({xs: [], ys: [], zs: []});
    const [department, setDepartment] = useState("");
    const [month, setMonth] = useState("");
    const [week, setWeek] = useState("");
    const [active, setActive] = useState("");
    //-------------------------------------------------------------------------
    const exportFunc = () => {
        const workbook = new exceljs.Workbook();
        workbook.creator = user.userName;
        workbook.created = new Date();
        workbook.calcProperties.fullCalcOnLoad = true;
        setShowLoader(true);
        //---------------------------------------------------------------
            const worksheet = workbook.addWorksheet("Sheet1", {views: [{showGridLines: false}]});
            worksheet.state = "visible";
            worksheet.properties.defaultColWidth = 22.57;
            const rows = [];
            for(let i = 0; i < curData.xs.length; i++){
                rows.push([
                    curData.xs[i],
                    curData.ys[i],
                    curData.zs[i],
                    curData.ys[i] / curData.zs[i]
                ]);
            }
            const columns = [
                { name: "Date", filterButton: true, totalsRowLabel: "Average"},
                { name: "Worked", filterButton: true, totalsRowFunction: "average"},
                { name: "Total", filterButton: true, totalsRowFunction: "average"},
                { name: "Utilization", filterButton: true, totalsRowFunction: "average"},
            ];
            //---------------------------------------------------------------
            worksheet.addTable({
                name: `MyTable`,
                ref: "A1",
                totalsRow: true,
                
                style: {
                    showRowStripes: true,
                },
                columns, 
                rows,
            });
            worksheet.getColumn(4).eachCell(cell => {
                cell.numFmt = "0.00%";
            })
            worksheet.columns.forEach(column => {
                const minLength = 18;
                let maxLength = 0;
                column["eachCell"]({ includeEmpty: true }, cell => {
                    // exclude row 1 ----------
                    if(cell.row === 1) return;
                    //-------------------------
                    const columnLength = cell.value?
                    cell.value.toString().length * (cell.font?cell.font.size / 11 * 1.2:1.3)
                    :minLength;
                    if(columnLength > maxLength){
                        maxLength = columnLength;
                    }
                });
                column.width = maxLength < minLength ? minLength : maxLength;
            });
            worksheet.getRows(2, rows.length).forEach(row => {row.alignment = { vertical: "middle", horizontal: "center", wrapText: true }});
        
        workbook.xlsx.writeBuffer().then(buffer => {
            saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx }), `labor usage export ${(new MyDate()).s()}.xlsx`);
            setShowLoader(false);
        })
    }
    //-------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        if(!department || !month || active !== "Month") return;
        const getData = async() => {
            const formData = new MyFormData();
            formData.append("month", month);
            formData.append("department", department);
            //----------------------------------
            setShowLoading(true);
            const resData = await myAxios("laborUsage/readVechileByMonth.php", formData);
            if(!isMounted) return;
            setShowLoading(false);
            if(!resData) return;
            //----------------------------------
            const localData = resData.data.main;
            const users = resData.data.users;
            const xs = [];
            const ys = [];
            const zs = [];
            const startOfMonthStr = month + "-01";
            const startOfMonth = new MyDate(startOfMonthStr.replace(/-/g, "/"));
            while(startOfMonth.s().startsWith(month) && startOfMonth.s() <= (new MyDate()).s()){
                const dateStr = startOfMonth.s();
                xs.push(dateStr);
                ys.push(localData.filter(item => item.startTime === dateStr).map(item => item.labor).filter(onlyUnique).length);
                zs.push(users.filter(user => 
                    user.hireDate < dateStr &&
                    (!user.quitDate || user.quitDate > dateStr)
                ).length);
                startOfMonth.setDate(startOfMonth.getDate() + 1);
            }
            setData({xs: xs, ys: ys, zs: zs});
        }
        getData();
        return () => {
            isMounted = false;
        }
    }, [month, department])
    useEffect(() => {
        let isMounted = true;
        if(!department || !week || active !== "Week") return;
        const getData = async() => {
            const startDate = getDatefromWeekNum(week, 0);
            const endDate = getDatefromWeekNum(week, 6);
            const formData = new MyFormData();
            formData.append("startDate", startDate);
            formData.append("endDate", endDate);
            formData.append("week", week);
            formData.append("department", department);
            //----------------------------------
            setShowLoading(true);
            const resData = await myAxios("laborUsage/readVechileByWeek.php", formData);
            if(!isMounted) return;
            setShowLoading(false);
            if(!resData) return;
            //----------------------------------
            const localData = resData.data.main;
            const users = resData.data.users;
            const xs = [];
            const ys = [];
            const zs = [];
            const startOfWeek = new MyDate(startDate.replace(/-/g, "/"));
            const endOfWeek = new MyDate(endDate.replace(/-/g, "/"));
            if(!["L&M", "Standby"].includes(department)){
                while(startOfWeek <= endOfWeek){
                    const dateStr = startOfWeek.s();
                    xs.push(dateStr);
                    ys.push(localData.filter(item => item.startTime === dateStr).map(item => item.labor).filter(onlyUnique).length);
                    zs.push(users.filter(user => 
                        user.hireDate < dateStr &&
                        (!user.quitDate || user.quitDate > dateStr)
                    ).length);
                    startOfWeek.setDate(startOfWeek.getDate() + 1);
                }
            }
            else{
                while(startOfWeek <= endOfWeek){
                    const dateStr = startOfWeek.s();
                    xs.push(dateStr);
                    zs.push(users.filter(user => 
                        user.hireDate < dateStr &&
                        (!user.quitDate || user.quitDate > dateStr)
                    ).length);
                    startOfWeek.setDate(startOfWeek.getDate() + 1);
                }
                if(localData.length){
                    const matrix = localData.map(person => person[week] === ""?[0, 0, 0, 0, 0, 0, 0]:
                    (
                        department === "L&M"?
                        JSON.parse(person[week]).form.map(day => day.workStatus === "Work"?1:0):
                        (department === "Standby"?JSON.parse(person[week]).data.map(day => day.length > 1?1:0):
                        [0, 0, 0, 0, 0, 0, 0])
                    )
                    );
                    const arr = [];
                    for(let i = 0; i < 7; i++){
                        arr.push(matrix.map(a => a[i]).reduce(numberReducer, 0));
                    }
                    ys.push(...arr);
                }
            }
            setData({xs: xs, ys: ys, zs: zs});
        }
        getData();
        return () => {
            isMounted = false;
        }
    }, [week, department])
    useEffect(() => {
        document.title = tabTitle;
        return () => {
            document.title = appTitle;
        };
    }, [tabTitle]);
    //-------------------------------------------------------------------------
    return (
    <div>
        <div className = "cssTab">
            <div className = "cssHeader" style = {{background: user.userTheme}}>
                <span>
                    <Resize/>
                    {tabTitle}
                </span>
            </div>
            <div className = "cssContent pb10">
                {showLoading?
                <div className = "cssLoading">Loading...</div>:
                <>
                <div className = "d-flex align-items-center mb-3">
                    <label className = "mb-0 mr-2 alignSelf-center">Mode</label>
                    <select
                        required
                        className = "mr-3 form-control inline"
                        value = {active}
                        onChange = {e => setActive(e.currentTarget.value)}
                    >
                        <option value = "">Select</option>
                        <option>Week</option>
                        <option>Month</option>
                    </select>
                    {active === "Month"?
                    <input
                        required
                        className = {"mr-3 form-control inline"}
                        type = "month"
                        value = {month}
                        max = {(new MyDate()).s().slice(0, 7)}
                        onChange = {e => {
                            setMonth(e.currentTarget.value);
                        }}
                    />
                    :<></>}
                    {active === "Week"?
                    <select
                        required
                        className = {"mr-3 form-control inline"}
                        value = {week}
                        onChange = {e => {
                            setWeek(e.currentTarget.value);
                        }}
                    >
                        <option value = {""}>Select</option>
                        {weeks.map((wk, key) => <option key = {key}>{wk}</option>)}
                    </select>
                    :<></>}

                    <label className = "mb-0 mr-2 alignSelf-center">Department</label>
                    <select
                        required
                        className = "form-control inline"
                        value = {department}
                        onChange = {e => {
                            setDepartment(e.currentTarget.value);
                        }}
                    >
                        <option value = {""}>Select</option>
                        <optgroup label = "In House">
                            {departments.map((d, key) => <option key = {key}>{d}</option>)}
                        </optgroup>
                        <optgroup label = "Outside Service">
                            <option>L&M</option>
                            <option>Standby</option>
                        </optgroup>
                    </select>
                    {true?
                    <span className = "ml-3 btn btn-primary" onClick = {exportFunc}>
                        Export
                    </span>:<></>}
                </div>
                {((active === "Month" && month) || (active === "Week" && week)) && department?
                <div className = "flex-grow-1">
                    <Chart
                        myChart = {myChart}
                        chartConfig = {{
                            type: "line",
                            data: {
                                labels: curData.xs,
                                datasets:  [{
                                    label: "Work",
                                    data: curData.ys,
                                    borderColor: 'rgb(75, 192, 192)',
                                    fill: false,
                                    tension: 0,
                                    // backgroundColor: 'rgb(75, 192, 192)'
                                },
                                {
                                    label: "Total",
                                    data: curData.zs,
                                    tension: 0,
                                    borderColor: 'rgb(54, 162, 235)', 
                                    fill: false, // "-1",
                                    // backgroundColor: 'rgb(75, 192, 192)'
                                }]
                            },
                            options: {
                                animation:{ onComplete },
                                hover: {
                                    animationDuration: 0
                                },
                                // tooltips: {
                                //     enabled: false
                                // },
                                legend: {
                                    display: true
                                },
                                title: {
                                    fontSize: 20,
                                    display: true, 
                                    text: `Labor Usage on ${active === "Month"?month:""}${active === "Week"?`${week} (${getDatefromWeekNum(week, 0)} ~ ${getDatefromWeekNum(week, 6)})`:""}`
                                },
                                scales: {
                                    xAxes: [{
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Date"
                                        }
                                    }],
                                    yAxes: [{
                                        ticks: {
                                            max: Math.ceil(1.1 * Math.max(...curData.zs)), 
                                            beginAtZero: true,
                                            callback: value => {if(value % 1 === 0) return value;} 
                                        },
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Number of People"
                                        },
                                    }]
                                } 
                            }
                        }}
                    />
                </div>
                :null}
                </>}
            </div>
        </div>    
    </div>
    )
}
// const response = await fetch("https://api.dictionaryapi.dev/api/v2/entries/en/gluttony");
// const data = await(response.json());